import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import {
    Box, TextField, InputAdornment, Button, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Modal, Typography, IconButton, Stack, MenuItem, Divider, TableSortLabel
} from "@mui/material";
import { Search, Delete, Edit, Add, CloudUpload, FileDownload, Close, BlockOutlined } from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCustomer,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    bulkAddCustomer
} from "../../redux/slices/customerSlice";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import { fetchCity, fetchRegion, fetchAllState } from "../../redux/slices/clientSlice";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import FilterBar2 from "./admin_dashboard/FilterBar2";

const ALL_FIELDS = [
    { id: "brand", label: "Brand", type: "select", opt: "brands" },
    { id: "customer_code", label: "Codes", type: "text" },
    { id: "customer_name", label: "Customer Name", type: "text" },
    { id: "parentAccount", label: "Parent Account", type: "text" },
    { id: "region", label: "Region", type: "select", opt: "regions" },
    { id: "state", label: "State", type: "select", opt: "states" },
    { id: "city", label: "City", type: "select", opt: "cities" },
    { id: "email_id", label: "Email ID 1", type: "text" },
    { id: "email2", label: "Email ID 2", type: "text" },
    { id: "mobile_no", label: "Contact 1", type: "text" },
    { id: "mobile_no2", label: "Contact 2", type: "text" },
    { id: "ownerName", label: "Owner Name", type: "text" },
    { id: "classificationFY", label: "Classification", type: "select", opt: "classification" },
    { id: "distributoFlag", label: "Distributor", type: "text" },
    { id: "address_gstbased", label: "Address", type: "text" },
    { id: "pinCode", label: "Pincode", type: "text" },
    { id: "gstNumber", label: "GST No", type: "text" },
    { id: "pan_no", label: "PAN No", type: "text" },
    { id: "tanNumber", label: "TAN No", type: "text" },
    { id: "revisedRebate", label: "Revised Rebate", type: "text" },
    { id: "channel", label: "Channel", type: "select", opt: "channel" },
    { id: "codeOfCreation", label: "Code of Creation", type: "date" },
    { id: "empCode", label: "Emp Code", type: "text" },
    { id: "empName", label: "Emp Name", type: "text" },
    { id: "empChannel", label: "Emp Channel", type: "text" },
    { id: "empDOJ", label: "Emp DOJ", type: "date" },
    { id: "empBaseLocation", label: "Base Location", type: "text" },
    { id: "status", label: "Active Status", type: "select", opt: "activeStatus" },
    { id: "renewalAgreement", label: "Renewal Agreement", type: "select", opt: "renewalAgreement" },
    { id: "finance", label: "Finance", type: "text" },
    { id: "oldEmployeeCode", label: "Old Emp Code", type: "text" },
    { id: "oldEmployeeName", label: "Old Emp Name", type: "text" },
];

const normalizeOption = (opt) => {
    if (opt === null || opt === undefined) return null;
    if (typeof opt === "object") {
        const val = opt.value !== undefined ? opt.value : opt.id;
        const lbl = opt.label !== undefined ? opt.label : opt.name || String(val);
        return { label: String(lbl), value: val };
    }
    return { label: String(opt), value: opt };
};

const REQUIRED_FIELDS = [
    "empCode",
    "empName",
    "empChannel",
    "empDOJ",
    "empBaseLocation",
    "status"
];

const FY_YEAR_OPTIONS = [
    { label: "2026-2027", value: "2026" },
    { label: "2025-2026", value: "2025" },
    { label: "2024-2025", value: "2024" },
    { label: "2023-2024", value: "2023" },
];

const Customer = () => {
    const { state } = useLocation();
    const dispatch = useDispatch();
    const { customerData, totalCount, getStatus, addStatus, updateStatus, bulkAddStatus, deleteStatus } = useSelector(state => state.customer);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: "customer_name", direction: "asc" });
    const fileInputRef = useRef(null);
    const { allStateData, regionData, cityData } = useSelector((state) => state.client);
    const [filters, setFilters] = useState({
        brand: [],
        classificationFY: [],
        channel: [],
        status: [],
        region: [],
        state: [],
        city: [],
        renewalAgreement: [],
        finance: []
    });

    const [month, setMonth] = useState(() => {
        if (state && state.months) return state.months;
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        return fullFYMonths.slice(0, currentMonthIndex + 1);
    });

    const [year, setYear] = useState(() => {
        if (state?.years?.length > 0) {
            const targetYrs = state.years.map(String);
            return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
        }
        const currentYrStr = String(new Date().getFullYear());
        return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
    });

    const hasValidFilters = Boolean(year?.length > 0 && month?.length > 0);

    const OPTIONS = {
        classification: [
            { label: "Silver", value: "Silver" },
            { label: "Gold", value: "Gold" },
            { label: "Platinum", value: "Platinum" },
            // { label: "Diamond", value: "Diamond" }
        ],
        channel: [
            { label: "Project", value: "Project" },
            { label: "Retail", value: "Retail" },
            { label: "Retail Project", value: "Retail Project" },
            { label: "Distribution", value: "Distribution" }
        ],
        activeStatus: [
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
            { label: "Close", value: "Close" }
        ],
        brands: [
            { label: "AS", value: "AS" },
            { label: "GROHE", value: "GROHE" }
        ],
        renewalAgreement: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
            { label: "Pending", value: "Pending" }
        ],
        // finance: [
        //     { label: "Type A", value: "Type A" },
        //     { label: "Type B", value: "Type B" },
        //     { label: "Credit", value: "Credit" },
        //     { label: "Cash", value: "Cash" }
        // ],

        regions: regionData?.map(r => ({
            label: r.name,
            value: r.name
        })) || [],

        states: allStateData?.map(s => ({
            label: s.name,
            value: s.name
        })) || [],

        cities: cityData?.map(c => ({
            label: c.name,
            value: c.name
        })) || [],
    };


    useEffect(() => {
        if (!hasValidFilters) {
            return;
        }
        const formattedFilters = {};
        Object.keys(filters).forEach(key => {
            if (Array.isArray(filters[key])) {
                formattedFilters[key] = filters[key].map(item => item.value).join(",");
            } else {
                formattedFilters[key] = filters[key] || "";
            }
        });

        dispatch(fetchCustomer({
            search: searchQuery,
            page: page + 1,
            rowsPerPage,
            sort_by: sortConfig.key,
            order: sortConfig.direction,
            month: month?.map(m => m)?.join(','),
            year: year?.map((y) => y.value)?.join(","),
            ...formattedFilters,
        }));
    }, [page, rowsPerPage, searchQuery, sortConfig, filters, month, year, hasValidFilters, dispatch]);

    useEffect(() => {
        dispatch(fetchRegion());
        dispatch(fetchAllState());
        dispatch(fetchCity());
    }, []);

    // useEffect(() => {
    //     dispatch(fetchCity({ id: cityData?.find(r => r.name === formData.state || filters.state)?.id }));
    // }, [formData.state, filters.state]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    const handleDownloadTemplate = () => {
        const headers = ALL_FIELDS.map(f => f.label);
        const exampleData = ["Brand A", "1001", "John Doe Enterprises", "Global Acc", "North", "Maharashtra", "Mumbai", "test@mail.com", "", "9876543210", "", "John Doe", "Gold", "Dist-1", "123 Street", "400001", "27AAAAA0000A1Z5", "ABCDE1234F", "", "10%", "Retail", "2024-01-01", "E001", "Alice", "Sales", "2023-05-10", "Mumbai", "Active", "Yes", "Type A", "", ""];
        const worksheet = XLSX.utils.aoa_to_sheet([headers, exampleData]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "Customer_Upload_Template.xlsx");
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });
            const wsname = wb.SheetNames[0];

            const rawData = XLSX.utils.sheet_to_json(wb.Sheets[wsname]);

            const formattedData = rawData.map((row) => {
                const newRow = {};
                ALL_FIELDS.forEach((field) => {
                    if (row[field.label] !== undefined) {
                        newRow[field.id] = row[field.label];
                    }
                });
                return newRow;
            });

            dispatch(bulkAddCustomer({ data: formattedData }));
            e.target.value = null;
        };
        reader.readAsBinaryString(file);
    };

    const [errors, setErrors] = useState({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;


    const validate = () => {
        let tempErrors = {};
        if (!formData.empCode) tempErrors.empCode = "EMP Code is required";
        if (!formData.empName) tempErrors.empName = "EMP Name is required";
        if (!formData.empChannel) tempErrors.empChannel = "EMP Channel is required";
        if (!formData.empDOJ) tempErrors.empDOJ = "EMP DOJ is required";
        if (!formData.empBaseLocation) tempErrors.empBaseLocation = "Base Location is required";
        if (!formData.status) tempErrors.status = "Status is required";

        if (formData.email_id && !emailRegex.test(formData.email_id)) {
            tempErrors.email_id = "Invalid Email format";
        }

        if (formData.email2 && !emailRegex.test(formData.email2)) {
            tempErrors.email2 = "Invalid Email format";
        }

        if (formData.mobile_no && !phoneRegex.test(formData.mobile_no)) {
            tempErrors.mobile_no = "Invalid Mobile Number";
        }

        if (formData.mobile_no2 && !phoneRegex.test(formData.mobile_no2)) {
            tempErrors.mobile_no2 = "Invalid Mobile Number";
        }


        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        if (editMode) {
            dispatch(updateCustomer(formData));
        } else {
            dispatch(addCustomer(formData));
        }
    };

    useEffect(() => {
        if (addStatus === "succeeded") {
            setOpen(false);
            setFormData({});
            const formattedFilters = {};
            Object.keys(filters).forEach(key => {
                if (Array.isArray(filters[key])) {
                    formattedFilters[key] = filters[key].map(item => item.value).join(",");
                } else {
                    formattedFilters[key] = filters[key] || "";
                }
            });

            dispatch(fetchCustomer({
                search: searchQuery,
                page: page + 1,
                rowsPerPage,
                sort_by: sortConfig.key,
                order: sortConfig.direction,
                month: month?.map(m => m)?.join(','),
                year: year?.map((y) => y.value)?.join(","),
                ...formattedFilters,
            }));
        }
    }, [addStatus])


    useEffect(() => {
        if (updateStatus === "succeeded") {
            setOpen(false);
            setFormData({});
            const formattedFilters = {};
            Object.keys(filters).forEach(key => {
                if (Array.isArray(filters[key])) {
                    formattedFilters[key] = filters[key].map(item => item.value).join(",");
                } else {
                    formattedFilters[key] = filters[key] || "";
                }
            });

            dispatch(fetchCustomer({
                search: searchQuery,
                page: page + 1,
                rowsPerPage,
                sort_by: sortConfig.key,
                order: sortConfig.direction,
                month: month?.map(m => m)?.join(','),
                year: year?.map((y) => y.value)?.join(","),
                ...formattedFilters,
            }));
        }
    }, [updateStatus])
    useEffect(() => {
        if (bulkAddStatus === "succeeded" || deleteStatus === "succeeded") {
            const formattedFilters = {};
            Object.keys(filters).forEach(key => {
                if (Array.isArray(filters[key])) {
                    formattedFilters[key] = filters[key].map(item => item.value).join(",");
                } else {
                    formattedFilters[key] = filters[key] || "";
                }
            });

            dispatch(fetchCustomer({
                search: searchQuery,
                page: page + 1,
                rowsPerPage,
                sort_by: sortConfig.key,
                order: sortConfig.direction,
                month: month?.map(m => m)?.join(','),
                year: year?.map((y) => y.value)?.join(","),
                ...formattedFilters,
            }));
        }
    }, [bulkAddStatus, deleteStatus])



    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this customer?")) {
            dispatch(deleteCustomer(id));
        }
    }

    const handleExport = () => {
        if (!customerData || customerData.length === 0) {
            alert("No data available to export");
            return;
        }

        const exportData = customerData.map((row, index) => {
            const formattedRow = { "S.No": page * rowsPerPage + index + 1 };

            ALL_FIELDS.forEach((field) => {
                formattedRow[field.label] = row[field.id] || "";
            });

            return formattedRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

        XLSX.writeFile(workbook, "Customer_List.xlsx");
    };

    const filterConfigs = [
        { id: "brand", label: "Brand", options: OPTIONS.brands, selected: filters.brand },
        { id: "classificationFY", label: "Classification", options: OPTIONS.classification, selected: filters.classificationFY },
        { id: "channel", label: "Channel", options: OPTIONS.channel, selected: filters.channel },
        { id: "status", label: "Active Status", options: OPTIONS.activeStatus, selected: filters.status },
        { id: "region", label: "Region", options: OPTIONS.regions, selected: filters.region },
        { id: "state", label: "State", options: OPTIONS.states, selected: filters.state },
        { id: "city", label: "City", options: OPTIONS.cities, selected: filters.city },
        { id: "renewalAgreement", label: "Renewal", options: OPTIONS.renewalAgreement, selected: filters.renewalAgreement },
        { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
    ];

    const handleMonthChange = (newMonths) => {
        setMonth(newMonths);
        if (newMonths?.length > 0 && year?.length === 0) {
            const currentYrStr = String(new Date().getFullYear());
            setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
        }
        setPage(0);
    };

    const handleFilterChange = (filterId, nextSelected) => {
        if (filterId === "year") {
            setYear(nextSelected);
            if (nextSelected.length === 0) {
                setMonth([]);
            } else if (month?.length === 0) {
                const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
                const currentCalendarMonth = new Date().getMonth() + 1;
                const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
                setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
            }
        } else {
            setFilters((prev) => ({
                ...prev,
                [filterId]: nextSelected,
            }));
        }
        setPage(0);
    };

    const handleClearFilters = () => {
        setFilters({
            brand: [],
            classificationFY: [],
            channel: [],
            status: [],
            region: [],
            state: [],
            city: [],
            renewalAgreement: [],
            finance: [],
        });
        setSearchQuery("");
        setPage(0);

        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

        const currentYrStr = String(new Date().getFullYear());
        setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    };

    return (
        <MainLayout title="Customer Management">

            <Box sx={{ p: 0 }}>
                <FilterBar2
                    filters={filterConfigs}
                    onFilterChange={handleFilterChange}
                    selectedMonth={month}
                    setSelectedMonth={handleMonthChange}
                    searchTerm={searchQuery}
                    setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
                    onClearFilters={handleClearFilters}
                    actions={
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outlined"
                                startIcon={<FileDownload />}
                                onClick={handleDownloadTemplate}
                                className="capitalize font-bold border-slate-300 text-slate-700 hover:bg-slate-50 transition-all"
                                sx={{
                                    py: "2px",
                                    px: "14px",
                                    borderRadius: 12,
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Template
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<CloudUpload />}
                                onClick={() => fileInputRef.current.click()}
                                disabled={bulkAddStatus === "pending"}
                                className="capitalize font-bold bg-slate-900 hover:bg-black text-white shadow-none transition-all"
                                sx={{
                                    py: "2px",
                                    px: "14px",
                                    borderRadius: 12,
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Upload
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept=".xlsx, .xls"
                                onChange={handleFileUpload}
                            />
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    setEditMode(false);
                                    setFormData({});
                                    setOpen(true);
                                }}
                                sx={{
                                    textTransform: "none",
                                    bgcolor: "#0288d1",
                                    py: "2px",
                                    px: "14px",
                                    borderRadius: 12,
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    boxShadow: "none",
                                    "&:hover": { bgcolor: "#01579b", boxShadow: "none" },
                                }}
                            >
                                Add New
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<TableChartIcon />}
                                onClick={handleExport}
                                sx={{
                                    textTransform: "none",
                                    bgcolor: "#2e7d32",
                                    py: "2px",
                                    px: "14px",
                                    borderRadius: 12,
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    boxShadow: "none",
                                    "&:hover": { bgcolor: "#1b5e20", boxShadow: "none" },
                                }}
                            >
                                Excel
                            </Button>
                        </div>
                    }
                />
                <Paper>
                    <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 240px)" }}>
                        <Table id="exceldata" stickyHeader>
                            <TableHead sx={{ background: "#04121f", color: "white" }}>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: "#04121f", color: "white" }}>S.No</TableCell>
                                    {ALL_FIELDS.map((f, index) => (
                                        <>
                                            <TableCell key={f.id} sx={{ bgcolor: "#04121f", color: "white", whiteSpace: "nowrap" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === f.id}
                                                    direction={sortConfig.key === f.id ? sortConfig.direction : "asc"}
                                                    onClick={() => handleSort(f.id)}
                                                    sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                                                >
                                                    {f.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        </>
                                    ))}
                                    <TableCell sx={{ bgcolor: "#04121f", color: "white", whiteSpace: "nowrap" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === "created_at"}
                                            direction={sortConfig.key === "created_at" ? sortConfig.direction : "asc"}
                                            onClick={() => handleSort("created_at")}
                                            sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                                        >
                                            Created At
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell sx={{ bgcolor: "#04121f", color: "white", whiteSpace: "nowrap" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === "updated_at"}
                                            direction={sortConfig.key === "updated_at" ? sortConfig.direction : "asc"}
                                            onClick={() => handleSort("updated_at")}
                                            sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                                        >
                                            Updated At
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            bgcolor: "#04121f",
                                            color: "white",
                                            position: "sticky",
                                            right: 0,
                                            zIndex: 11
                                        }}
                                    >Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    if (!hasValidFilters) {
                                        return (
                                            <TableRow>
                                                <TableCell colSpan={ALL_FIELDS.length + 4} align="center">
                                                    No data
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return (getStatus === "pending") ? (
                                        <TableBodySkeleton cols={ALL_FIELDS.length + 4} rows={10} hasActions />
                                    ) : customerData?.length > 0 ? (
                                        customerData.map((row, index) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                {ALL_FIELDS.map(f => <TableCell key={f.id} sx={{ whiteSpace: "nowrap" }}>{row[f.id] || "-"}</TableCell>)}
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {row.created_at
                                                        ? new Date(row.created_at)?.toLocaleString()
                                                        : "-"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {row.updated_at
                                                        ? new Date(row.updated_at)?.toLocaleString()
                                                        : "-"}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        position: "sticky",
                                                        right: 0,
                                                        bgcolor: "white",
                                                        zIndex: 1,
                                                        boxShadow: "-2px 0 5px rgba(0,0,0,0.1)"
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1}>
                                                        <IconButton size="small" onClick={() => { setEditMode(true); console.log(row); setFormData(row); setOpen(true); }}><Edit color="primary" fontSize="small" /></IconButton>
                                                        <IconButton size="small" onClick={() => handleDelete(row.id)}><Delete color="error" fontSize="small" /></IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow><TableCell colSpan={ALL_FIELDS.length + 4} align="center">No data found</TableCell></TableRow>
                                    );
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <TablePagination
                    rowsPerPageOptions={hasValidFilters ? [10, 25, 100, (totalCount || 0)] : [10, 25, 100]}
                    component="div"
                    count={hasValidFilters ? (totalCount || 0) : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ backgroundColor: "#fff", paddingTop: 2, paddingBottom: 2 }}

                />
            </Box>

            <Modal open={open} sx={{ zIndex: 1000 }}
            >
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95%', maxWidth: 1200, bgcolor: 'white', borderRadius: '12px', boxShadow: 24, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">{editMode ? "Edit Customer" : "New Customer Entry"}</Typography>
                        <IconButton onClick={() => setOpen(false)}><Close /></IconButton>
                    </Box>
                    <Box sx={{ p: 4, maxHeight: '75vh', overflowY: 'auto' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>General Information</Typography>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {ALL_FIELDS.slice(0, 22).map(field => (
                                <TextField
                                    required={REQUIRED_FIELDS.includes(field.id)}
                                    key={field.id}
                                    label={field.label}
                                    select={field.type === "select"}
                                    type={field.type === "date"}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData[field.id] || ""}
                                    error={!!errors[field.id]}
                                    helperText={errors[field.id]}
                                    onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                >
                                    {field.type === "select" &&
                                        OPTIONS[field.opt]?.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            ))}
                        </div>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>Employee & Internal Assignment</Typography>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {ALL_FIELDS.slice(22).map(field => (
                                <TextField
                                    required={REQUIRED_FIELDS.includes(field.id)}
                                    key={field.id}
                                    label={field.label}
                                    select={field.type === "select"}
                                    type={field.type === "date" ? "date" : "text"}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData[field.id] || ""}
                                    error={!!errors[field.id]}
                                    helperText={errors[field.id]}
                                    onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                >
                                    {field.type === "select" &&
                                        OPTIONS[field.opt]?.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            ))}
                        </div>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => setOpen(false)} variant="outlined">Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{ bgcolor: "#0288d1", px: 4 }}
                            disabled={addStatus === 'pending' || updateStatus === 'pending'}
                        >
                            {editMode ? "Update" : "Save"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </MainLayout>
    );
};

export default Customer;